import { useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { DialogueStep, DialogueStepInsert } from '@/services/chatbot-config'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import { Loader2, Plus, Trash2 } from 'lucide-react'

const optionSchema = z.object({
  label: z.string().min(1, 'Label required'),
  value: z.string().min(1, 'Value required'),
  next_step: z.string().optional(), // Can be empty if it's a leaf node or logic handled elsewhere
  score: z.coerce.number().default(0),
})

const formSchema = z.object({
  message_text: z.string().min(1, 'Message is required'),
  options: z.array(optionSchema).optional(),
})

interface StepDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialData: DialogueStep | null
  onSubmit: (data: Partial<DialogueStepInsert>) => Promise<void>
  isSubmitting: boolean
}

export const StepDialog = ({
  open,
  onOpenChange,
  initialData,
  onSubmit,
  isSubmitting,
}: StepDialogProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      message_text: '',
      options: [],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'options',
  })

  useEffect(() => {
    if (initialData) {
      form.reset({
        message_text: initialData.message_text,
        options: initialData.options || [],
      })
    }
  }, [initialData, form, open])

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    await onSubmit({
      message_text: values.message_text,
      options: values.options,
    })
  }

  if (!initialData) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Step: {initialData.step_identifier}</DialogTitle>
          <DialogDescription>
            Modify the bot question and available options.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="message_text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bot Message</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="What the bot says..."
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <FormLabel>User Options</FormLabel>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    append({ label: '', value: '', next_step: '', score: 0 })
                  }
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Option
                </Button>
              </div>
              <FormDescription>
                Define the buttons the user can click. Leave Next Step empty if
                handled by logic.
              </FormDescription>

              <div className="space-y-3">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 border rounded-md bg-slate-50 items-start"
                  >
                    <div className="md:col-span-4">
                      <FormField
                        control={form.control}
                        name={`options.${index}.label`}
                        render={({ field }) => (
                          <FormItem className="space-y-1">
                            <FormLabel className="text-xs">Label</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="e.g. Yes" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <FormField
                        control={form.control}
                        name={`options.${index}.value`}
                        render={({ field }) => (
                          <FormItem className="space-y-1">
                            <FormLabel className="text-xs">Value</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="e.g. yes" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <FormField
                        control={form.control}
                        name={`options.${index}.next_step`}
                        render={({ field }) => (
                          <FormItem className="space-y-1">
                            <FormLabel className="text-xs">
                              Next Step ID
                            </FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="e.g. MATURITY" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="md:col-span-1">
                      <FormField
                        control={form.control}
                        name={`options.${index}.score`}
                        render={({ field }) => (
                          <FormItem className="space-y-1">
                            <FormLabel className="text-xs">Score</FormLabel>
                            <FormControl>
                              <Input {...field} type="number" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="md:col-span-1 pt-6 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700 h-8 w-8"
                        onClick={() => remove(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {fields.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4 border border-dashed rounded-md">
                    No options defined. This implies a free-text input step or
                    end of flow.
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Step
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
